import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { WorkflowTemplateOrganisationAccessLevel } from "../../../../../constants";
import { ProjectDatesSchema } from "../../../../../models";
import { GetWorkflowTemplateResponse, searchStandards } from "../../../../../service/query";
import { getWorkflowTemplate } from "../../../../../service/query/QueryService.full";
import { Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../useAuth";
import { Toast } from "../../../../../widget";
import { WorkflowTemplateStepModel } from "./components";

type WorkflowTemplatePageRouteParams = "workflowTemplateUuid";

interface WorkflowTemplateProviderProps {
  children?: JSX.Element;
}

interface WorkflowTemplateContextType {
  workflowTemplateUuid: string | undefined;
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  workflowTemplatePhases: {
    keyDates: string[];
    phase: string;
    sequence: number;
  }[];
  workflowTemplateSteps: WorkflowTemplateStepModel[] | undefined;
  dateSchemas: ProjectDatesSchema[];
  workflowTemplateOrganisationAccessLevel: WorkflowTemplateOrganisationAccessLevel | undefined;

  projectPhase: string;
  handlePhaseChange: (phase: string) => void;

  hasAccessLevelAndCanEditTemplate: (accessLevel: WorkflowTemplateOrganisationAccessLevel) => boolean;
  canEditStepOrTask: (createdByOrganisationUuid: string | null) => Promise<boolean>;

  refreshWorkflowTemplate: boolean;
  shouldRefreshWorkflowTemplate: () => void;
}

export const WorkflowTemplateContext = createContext<WorkflowTemplateContextType>({} as WorkflowTemplateContextType);

const normaliseWorkflowTemplatePhases = (
  phases: GetWorkflowTemplateResponse["phases"] | undefined
): WorkflowTemplateContextType["workflowTemplatePhases"] => {
  return (
    phases
      ?.filter((item) => item.phase.trim())
      .sort((leftItem, rightItem) => leftItem.sequence - rightItem.sequence)
      .map((item) => ({
        keyDates: item.keyDates,
        phase: item.phase.trim(),
        sequence: item.sequence,
      })) ?? []
  );
};

export const WorkflowTemplateProvider = ({ children }: WorkflowTemplateProviderProps): JSX.Element => {
  const { currentOrganisationUuid } = useAuth();
  const { workflowTemplateUuid } = useParams<WorkflowTemplatePageRouteParams>();

  const [isLoading, setIsLoading] = useState(true);

  const [refreshWorkflowTemplate, setRefreshWorkflowTemplate] = useState(false);
  const shouldRefreshWorkflowTemplate = (): void => {
    setRefreshWorkflowTemplate((prev) => !prev);
  };

  const [workflowTemplate, setWorkflowTemplate] = useState<GetWorkflowTemplateResponse | undefined>();
  const [workflowTemplateSteps, setWorkflowTemplateSteps] = useState<WorkflowTemplateStepModel[] | undefined>();
  const [dateSchemas, setDateSchemas] = useState<ProjectDatesSchema[]>([]);
  const [workflowTemplateOrganisationAccessLevel, setWorkflowTemplateOrganisationAccessLevel] =
    useState<WorkflowTemplateOrganisationAccessLevel>();

  const [projectPhase, setProjectPhase] = useState<string>("");
  const workflowTemplatePhases = useMemo(
    () => normaliseWorkflowTemplatePhases(workflowTemplate?.phases),
    [workflowTemplate?.phases]
  );

  const handlePhaseChange = useCallback((phase: string): void => {
    setProjectPhase(phase);
  }, []);

  const hasAccessLevelAndCanEditTemplate = (accessLevel: WorkflowTemplateOrganisationAccessLevel): boolean => {
    // If not just viewing, if expired, return false
    if (
      workflowTemplateOrganisationAccessLevel === undefined ||
      (accessLevel !== WorkflowTemplateOrganisationAccessLevel.Viewer &&
        workflowTemplate?.validTo != null &&
        workflowTemplate.validTo <= new Date())
    ) {
      return false;
    }

    const accessLevels = {
      [WorkflowTemplateOrganisationAccessLevel.Owner]: [
        WorkflowTemplateOrganisationAccessLevel.Owner,
        WorkflowTemplateOrganisationAccessLevel.Author,
        WorkflowTemplateOrganisationAccessLevel.Contributor,
        WorkflowTemplateOrganisationAccessLevel.Viewer,
      ],
      [WorkflowTemplateOrganisationAccessLevel.Author]: [
        WorkflowTemplateOrganisationAccessLevel.Author,
        WorkflowTemplateOrganisationAccessLevel.Contributor,
        WorkflowTemplateOrganisationAccessLevel.Viewer,
      ],
      [WorkflowTemplateOrganisationAccessLevel.Contributor]: [
        WorkflowTemplateOrganisationAccessLevel.Contributor,
        WorkflowTemplateOrganisationAccessLevel.Viewer,
      ],
      [WorkflowTemplateOrganisationAccessLevel.Viewer]: [WorkflowTemplateOrganisationAccessLevel.Viewer],
    };

    return accessLevels[workflowTemplateOrganisationAccessLevel]?.includes(accessLevel);
  };

  /*  Check if user can edit step or task:
      Owner + Author can edit all steps and tasks,
      Contributor can only edit their own steps and tasks created by there org
  */
  const canEditStepOrTask = async (createdByOrganisationUuid: string | null): Promise<boolean> => {
    if (workflowTemplateOrganisationAccessLevel) {
      if (hasAccessLevelAndCanEditTemplate(WorkflowTemplateOrganisationAccessLevel.Author)) {
        return true;
      }
      if (
        hasAccessLevelAndCanEditTemplate(WorkflowTemplateOrganisationAccessLevel.Contributor) &&
        currentOrganisationUuid !== undefined &&
        currentOrganisationUuid === createdByOrganisationUuid
      ) {
        return true;
      }
    }
    return false;
  };

  const fetchData = useCallback(async () => {
    if (workflowTemplateUuid) {
      const res = await getWorkflowTemplate({
        workflowTemplateUuid,
      });
      if (res.status === Status.Success && res.data) {
        setWorkflowTemplate(res.data);

        setWorkflowTemplateOrganisationAccessLevel(
          (res.data.organisations.find((x) => x.uuid === currentOrganisationUuid)
            ?.accessLevel as WorkflowTemplateOrganisationAccessLevel) ?? WorkflowTemplateOrganisationAccessLevel.Viewer
        );
      } else if (res.status === Status.Error && res.errors && res.errors.length) {
        Toast.error({ message: getErrorMessageFromCode(res.errors[0].message) });
      }
    }
  }, [workflowTemplateUuid, refreshWorkflowTemplate]);

  const parseProjectTypeDateSchema = useCallback((dateSchema: unknown): ProjectDatesSchema | undefined => {
    if (!dateSchema) {
      return undefined;
    }

    try {
      const parsedSchema = typeof dateSchema === "string" ? JSON.parse(dateSchema) : dateSchema;
      return (parsedSchema as { properties?: ProjectDatesSchema }).properties;
    } catch {
      return undefined;
    }
  }, []);

  const loadDateSchemasByProjectType = useCallback(async (): Promise<void> => {
    const workflowTemplateProjectTypeUuid = workflowTemplate?.projectType?.uuid;

    if (!workflowTemplateProjectTypeUuid) {
      setDateSchemas([]);
      return;
    }

    const standardsResponse = await searchStandards({
      paging: {
        limit: 50,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: {
        results: {
          projectTypes: {
            some: {
              uuid: {
                operator: "eq",
                value: workflowTemplateProjectTypeUuid,
              },
            },
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    });

    if (standardsResponse.status !== Status.Success || !standardsResponse.data?.results.length) {
      setDateSchemas([]);
      return;
    }

    const matchingDateSchemas = standardsResponse.data.results
      .flatMap((standard) => standard.projectTypes)
      .filter((projectType) => projectType.uuid === workflowTemplateProjectTypeUuid)
      .map((projectType) => parseProjectTypeDateSchema(projectType.dateSchema))
      .filter((schema): schema is ProjectDatesSchema => schema !== undefined);

    const uniqueDateSchemas = Array.from(
      new Map(matchingDateSchemas.map((schema) => [JSON.stringify(schema), schema])).values()
    );
    setDateSchemas(uniqueDateSchemas);
  }, [workflowTemplate?.projectType?.uuid, parseProjectTypeDateSchema]);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData, workflowTemplateUuid, refreshWorkflowTemplate]);

  useEffect(() => {
    loadDateSchemasByProjectType();
  }, [loadDateSchemasByProjectType]);

  useEffect(() => {
    const availablePhases = workflowTemplatePhases.map((item) => item.phase);

    setProjectPhase((currentPhase) => {
      if (availablePhases.includes(currentPhase)) {
        return currentPhase;
      }

      return availablePhases[0] ?? "";
    });
  }, [workflowTemplatePhases]);

  useEffect(() => {
    setWorkflowTemplateSteps(workflowTemplate?.steps.filter((step) => step.defaultPhase === projectPhase));
  }, [projectPhase, workflowTemplate]);

  const memoisedValue = useMemo(
    () => ({
      workflowTemplateUuid,
      workflowTemplate,
      workflowTemplatePhases,
      workflowTemplateSteps,
      dateSchemas,
      workflowTemplateOrganisationAccessLevel,

      projectPhase,
      handlePhaseChange,

      hasAccessLevelAndCanEditTemplate,
      canEditStepOrTask,

      refreshWorkflowTemplate,
      shouldRefreshWorkflowTemplate,
    }),
    [
      workflowTemplate,
      workflowTemplatePhases,
      workflowTemplateSteps,
      dateSchemas,
      refreshWorkflowTemplate,
      projectPhase,
      handlePhaseChange,
      fetchData,
    ]
  );

  return (
    <WorkflowTemplateContext.Provider value={memoisedValue}>{!isLoading && children}</WorkflowTemplateContext.Provider>
  );
};
